"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Link } from "@/i18n/navigation";
import { $api } from "@/lib/api-client";
import {
  clearPendingFinalize,
  type PendingFinalize,
  readPendingFinalize,
} from "@/lib/checkout/pending-finalize";
import { CheckoutSuccessContent } from "./CheckoutSuccessContent";

type Status = "loading" | "finalizing" | "success" | "error" | "missing";

/**
 * A finalize that fails because the preparation is already consumed means the
 * order was ALREADY recorded. The backend returns 404 ("Preparation not found or
 * already used") for this — it is NOT a silent no-op — so the client enforces
 * idempotency itself. In this flow the preparation always existed (we created it
 * during checkout), so a 404 here means "already finalized", not "never existed".
 */
function isAlreadyFinalized(error: unknown): boolean {
  const e = error as
    | { statusCode?: number; status?: number; message?: unknown }
    | null
    | undefined;
  const code = e?.statusCode ?? e?.status;
  const message = typeof e?.message === "string" ? e.message : "";
  return code === 404 || /already used|not found/i.test(message);
}

/**
 * Owns the post-payment order finalize. Reads the payload persisted by the
 * checkout flow, replays POST /order, and renders three soft states around an
 * always-happy hero — the warning must never kill the celebration. The finalize
 * is made idempotent client-side (a redundant finalize, e.g. from a remount or a
 * Retry after a lost-but-successful response, resolves to success — see
 * isAlreadyFinalized). With no payload (fresh tab / already finalized) it shows a
 * neutral "processing" message linking to My Orders.
 */
export const CheckoutSuccessView = () => {
  const t = useTranslations("checkout");
  const tc = useTranslations("common");
  const [status, setStatus] = useState<Status>("loading");
  const [payload, setPayload] = useState<PendingFinalize | null>(null);
  const firedRef = useRef(false);
  const { mutate } = $api.useMutation("post", "/order");

  const runFinalize = (p: PendingFinalize) => {
    setStatus("finalizing");
    mutate(
      { body: p.order },
      {
        onSuccess: () => {
          clearPendingFinalize();
          setStatus("success");
        },
        onError: (error: unknown) => {
          // Already-finalized (404 "already used") is a success, not a failure —
          // the order is recorded. Only genuine failures (network/4xx/5xx where
          // the order was NOT created) show the gentle failure card.
          if (isAlreadyFinalized(error)) {
            clearPendingFinalize();
            setStatus("success");
            return;
          }
          setStatus("error");
        },
      },
    );
  };

  // On mount: read the payload and kick off the finalize exactly once.
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const p = readPendingFinalize();
    if (!p) {
      setStatus("missing");
      return;
    }
    setPayload(p);
    runFinalize(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "loading") {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="size-6" aria-label={tc("loading")} />
      </div>
    );
  }

  if (status === "missing") {
    return (
      <Card className="mx-auto max-w-md">
        <CardContent className="space-y-4 p-6 text-center">
          <p className="text-sm text-text-primary">{t("success.processing")}</p>
          <Button asChild colors="primary" className="w-full">
            <Link href="/profile/orders">{t("success.viewMyOrders")}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-6">
      <CheckoutSuccessContent itemCount={payload?.itemCount ?? 1} />

      {status === "finalizing" && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
        >
          <Spinner className="size-4" aria-hidden />
          <span>{t("success.finalizing")}</span>
        </div>
      )}

      {status === "success" && (
        <>
          <p className="sr-only" role="status" aria-live="polite">
            {t("success.recorded")}
          </p>
          <Button asChild colors="primary" className="w-full">
            <Link href="/profile/orders">{t("success.viewMyOrders")}</Link>
          </Button>
        </>
      )}

      {status === "error" && (
        <Card className="border-stroke-light">
          <CardContent role="alert" className="space-y-3 p-4 text-center">
            <p className="text-sm font-semibold text-text-primary">
              {t("success.errorTitle")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("success.errorBody")}
            </p>
            {payload?.order.tx_hash && (
              <p className="font-inter text-xs text-muted-foreground" dir="ltr">
                {payload.order.tx_hash}
              </p>
            )}
            <Button
              colors="primary"
              className="w-full"
              onClick={() => payload && runFinalize(payload)}
            >
              {tc("retry")}
            </Button>
            <Button
              asChild
              colors="secondary"
              variant="light"
              className="w-full"
            >
              <Link href="/profile/orders">{t("success.viewMyOrders")}</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
